import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const location = useLocation();
  const navigate = useNavigate();

  const navigationItems = [
    { 
      label: 'Dashboard', 
      path: '/dashboard-overview', 
      icon: 'LayoutDashboard',
      description: 'Operational overview and task management'
    },
    { 
      label: 'Cost Calculator', 
      path: '/branding-cost-calculator', 
      icon: 'Calculator',
      description: 'Branding cost estimation and planning'
    },
    { 
      label: 'Installations', 
      path: '/installation-tracking', 
      icon: 'Wrench',
      description: 'Installation lifecycle management'
    },
    { 
      label: 'Store Layouts', 
      path: '/store-layout-manager', 
      icon: 'Layout',
      description: 'Layout documentation and version control'
    },
    { 
      label: 'Analytics', 
      path: '/brand-analytics', 
      icon: 'BarChart3',
      description: 'Business intelligence and reporting'
    }
  ];

  const secondaryItems = [
    { 
      label: 'Store Management', 
      path: '/store-management', 
      icon: 'Store',
      description: 'Store data maintenance'
    },
    { 
      label: 'Key Visual Management', 
      path: '/kv', 
      icon: 'Image',
      description: 'Brand assets and visual content management'
    }
  ];

  const handleNavigation = (path) => {
    navigate(path);
    setIsMenuOpen(false);
  };

  const handleSearch = (e) => {
    e?.preventDefault();
    if (searchQuery?.trim()) {
      console.log('Search query:', searchQuery);
      setSearchQuery('');
      setIsSearchOpen(false);
    }
  };

  const toggleSearch = () => {
    setIsSearchOpen(!isSearchOpen);
    if (!isSearchOpen) {
      setTimeout(() => {
        document.getElementById('search-input')?.focus();
      }, 100);
    }
  };

  const isActivePath = (path) => location?.pathname === path;

  return (
    <>
      {/* Status Indicator Bar */}
      <div className="bg-warning/10 border-b border-warning/20 px-4 py-2 text-sm text-warning-foreground">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <div className="flex items-center gap-2">
            <Icon name="AlertTriangle" size={16} className="text-warning" />
            <span className="text-warning font-medium">3 installations pending approval</span>
          </div>
          <Button variant="ghost" size="xs" className="text-warning hover:bg-warning/20">
            <Icon name="X" size={14} />
          </Button>
        </div>
      </div>
      {/* Main Header */}
      <header className="bg-surface border-b border-border sticky top-0 z-50 shadow-soft">
        <div className="flex items-center justify-between h-16 px-4 max-w-7xl mx-auto">
          {/* Logo */}
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                <Icon name="Store" size={20} className="text-primary-foreground" />
              </div>
              <div className="flex flex-col">
                <span className="text-lg font-semibold text-foreground leading-none">BrandStore</span>
                <span className="text-xs text-muted-foreground leading-none">Manager</span>
              </div>
            </div>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1">
            {navigationItems?.map((item) => (
              <Button
                key={item?.path}
                variant={isActivePath(item?.path) ? "default" : "ghost"}
                size="sm"
                onClick={() => handleNavigation(item?.path)}
                className={`gap-2 transition-all duration-200 ${
                  isActivePath(item?.path) 
                    ? 'bg-primary text-primary-foreground shadow-sm' 
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
              >
                <Icon name={item?.icon} size={16} />
                {item?.label}
              </Button>
            ))}
            
            {/* More Menu */}
            <div className="relative ml-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="gap-2 text-muted-foreground hover:text-foreground hover:bg-muted"
              >
                <Icon name="MoreHorizontal" size={16} />
                More
              </Button>
              
              {isMenuOpen && (
                <div className="absolute right-0 top-full mt-2 w-56 bg-popover border border-border rounded-lg shadow-elevated animate-slide-down">
                  <div className="p-2">
                    {secondaryItems?.map((item) => (
                      <Button
                        key={item?.path}
                        variant="ghost"
                        size="sm"
                        onClick={() => handleNavigation(item?.path)}
                        className={`w-full justify-start gap-3 ${
                          isActivePath(item?.path) 
                            ? 'bg-primary/10 text-primary' :'text-muted-foreground hover:text-foreground hover:bg-muted'
                        }`}
                      >
                        <Icon name={item?.icon} size={16} />
                        <div className="flex flex-col items-start">
                          <span className="text-sm font-medium">{item?.label}</span>
                          <span className="text-xs text-muted-foreground">{item?.description}</span>
                        </div>
                      </Button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </nav>

          {/* Search and Actions */}
          <div className="flex items-center gap-2">
            {/* Search */}
            <div className="relative">
              {isSearchOpen ? (
                <form onSubmit={handleSearch} className="flex items-center">
                  <input
                    id="search-input"
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e?.target?.value)}
                    placeholder="Search stores, installations..."
                    className="w-64 px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent animate-fade-in"
                    onBlur={() => {
                      if (!searchQuery?.trim()) {
                        setIsSearchOpen(false);
                      }
                    }}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      setSearchQuery('');
                      setIsSearchOpen(false);
                    }}
                    className="ml-2"
                  >
                    <Icon name="X" size={16} />
                  </Button>
                </form>
              ) : (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={toggleSearch}
                  className="text-muted-foreground hover:text-foreground"
                >
                  <Icon name="Search" size={20} />
                </Button>
              )}
            </div>

            {/* Mobile Menu Toggle */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="lg:hidden text-muted-foreground hover:text-foreground"
            >
              <Icon name={isMenuOpen ? "X" : "Menu"} size={20} />
            </Button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="lg:hidden border-t border-border bg-surface animate-slide-down">
            <div className="p-4 space-y-2">
              {[...navigationItems, ...secondaryItems]?.map((item) => (
                <Button
                  key={item?.path}
                  variant={isActivePath(item?.path) ? "default" : "ghost"}
                  size="sm"
                  onClick={() => handleNavigation(item?.path)}
                  className={`w-full justify-start gap-3 ${
                    isActivePath(item?.path) 
                      ? 'bg-primary text-primary-foreground' 
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                  }`}
                >
                  <Icon name={item?.icon} size={16} />
                  <div className="flex flex-col items-start">
                    <span className="text-sm font-medium">{item?.label}</span>
                    <span className="text-xs opacity-75">{item?.description}</span>
                  </div>
                </Button>
              ))}
            </div>
          </div>
        )}
      </header>
    </>
  );
};

export default Header;